import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY } from '@/utils/language';

interface LanguageSwitcherProps {
  /** Show the "Don't speak Polish? We've got you covered" footer (logged-out top nav). */
  showHint?: boolean;
  align?: 'start' | 'end';
  className?: string;
}

const LanguageSwitcher = ({ showHint = false, align = 'end', className }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation();

  const handleChange = (lng: string) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    } catch {
      /* persistence is best-effort; ignore storage failures */
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className={`gap-1.5 text-muted-foreground ${className ?? ''}`}>
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{t(`language_switcher.${i18n.language}`)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onClick={() => handleChange(lng)}
            className={i18n.language === lng ? 'font-semibold bg-accent/50' : ''}
          >
            {t(`language_switcher.${lng}`)}
          </DropdownMenuItem>
        ))}
        {showHint && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              <p>{t('language_switcher.hint_question')}</p>
              <p className="font-medium underline underline-offset-2">{t('language_switcher.hint_answer')}</p>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
